import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Package, Calendar, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const InventoryDamageReport = () => {
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/inventory/damaged-items');
      setDamages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
      setDamages([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data
  const getFilteredData = () => {
    let filtered = [...damages];
    
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }
    
    if (startDate) {
      filtered = filtered.filter(d => d.damage_date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(d => d.damage_date <= endDate);
    }
    
    return filtered;
  };

  const filteredDamages = getFilteredData();

  // Calculate summary
  const totalLoss = filteredDamages.reduce((sum, d) => sum + (d.total_loss || 0), 0);
  const totalQuantity = filteredDamages.reduce((sum, d) => sum + (d.quantity || 0), 0);
  const resolvedItems = filteredDamages.filter(d => d.status === 'Resolved');
  const resolvedLoss = resolvedItems.reduce((sum, d) => sum + (d.total_loss || 0), 0);
  const pendingItems = filteredDamages.filter(d => d.status !== 'Resolved');

  // Get unique statuses for filter
  const statuses = [...new Set(damages.map(d => d.status).filter(Boolean))];

  const getStatusBadge = (status) => {
    const colors = {
      'Reported': 'bg-red-100 text-red-800',
      'Under Investigation': 'bg-yellow-100 text-yellow-800',
      'Resolved': 'bg-green-100 text-green-800',
      'Written Off': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-red-100 text-red-800';
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Inventory Damage Report
          </CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="w-36"
              />
              <span className="text-gray-500">to</span>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="w-36"
              />
            </div>
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Total Damaged Items</p>
              <p className="text-2xl font-bold text-red-700">{filteredDamages.length}</p>
              <p className="text-xs text-red-500 mt-1">Qty: {totalQuantity}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Total Loss</p>
              <p className="text-2xl font-bold text-orange-700">₹{totalLoss.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Resolved</p>
              <p className="text-2xl font-bold text-green-700">{resolvedItems.length}</p>
              <p className="text-xs text-green-500 mt-1">₹{resolvedLoss.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-700">{pendingItems.length}</p>
              <p className="text-xs text-yellow-500 mt-1">₹{(totalLoss - resolvedLoss).toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">
            Showing {filteredDamages.length} damage records
          </div>

          {/* Data Table - Matching DamageEntry History */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3">Damage #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Item</th>
                  <th className="text-center p-3">Quantity</th>
                  <th className="text-right p-3">Loss</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">Responsible</th>
                  <th className="text-left p-3">Action Taken</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredDamages.length === 0 ? (
                  <tr><td colSpan="9" className="text-center p-8 text-gray-500">No damage records found.</td></tr>
                ) : (
                  filteredDamages.map((damage, idx) => (
                    <tr key={damage.id || idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{damage.damage_number || '-'}</td>
                      <td className="p-3">{damage.damage_date ? formatDate(damage.damage_date) : '-'}</td>
                      <td className="p-3">{damage.item_name || '-'}</td>
                      <td className="p-3 text-center">{damage.quantity || 0}</td>
                      <td className="p-3 text-right text-red-600 font-semibold">
                        ₹{(damage.total_loss || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3 text-sm">{damage.damage_reason || '-'}</td>
                      <td className="p-3 text-sm">{damage.responsible_person || '-'}</td>
                      <td className="p-3 text-sm">{damage.action_taken || '-'}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(damage.status)}`}>
                          {damage.status || 'Reported'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Summary by Status */}
          {filteredDamages.length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Summary by Status
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Reported', 'Under Investigation', 'Resolved', 'Written Off'].map(status => {
                  const statusItems = filteredDamages.filter(d => d.status === status);
                  const statusLoss = statusItems.reduce((sum, d) => sum + (d.total_loss || 0), 0);
                  if (statusItems.length === 0) return null;
                  return (
                    <div key={status} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(status)}`}>{status}</span>
                      <p className="text-gray-600 mt-2">Items: {statusItems.length}</p>
                      <p className="text-red-600 font-semibold">₹{statusLoss.toLocaleString('en-IN')}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryDamageReport;
